import { ArrowLeft, Clock, Heart, Smartphone, Coffee, Users, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function QualityTimeDateGuide() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-violet-700 hover:text-violet-800 hover:bg-violet-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Week
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-violet-100 rounded-full">
              <Clock className="w-8 h-8 text-violet-600" />
            </div>
            <div>
              <h1 className="text-3xl font-serif font-bold text-gray-900">Quality Time Date</h1>
              <p className="text-violet-600">Week 10, Day 3 - Social Connections</p>
            </div>
          </div>

          <div className="prose prose-violet max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              In our distracted world, giving someone your full, undivided attention is one of the greatest 
              gifts you can offer. Today, schedule and enjoy an uninterrupted hour with someone important to you.
            </p>

            <div className="bg-violet-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-violet-800 mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5" />
                The Power of Presence
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Deepens emotional connection and trust</li>
                <li>• Creates lasting, meaningful memories</li>
                <li>• Shows the other person they truly matter</li>
                <li>• Reduces feelings of isolation</li>
                <li>• Strengthens your support network</li>
              </ul>
            </div>

            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">Quality Time Ideas</h2>
            
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <div className="bg-white border-2 border-violet-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Coffee className="w-5 h-5 text-violet-600" />
                  <h3 className="font-semibold text-gray-900">Coffee or Tea Date</h3>
                </div>
                <p className="text-gray-600 text-sm">Meet at a quiet cafe where you can talk without interruptions.</p>
              </div>
              
              <div className="bg-white border-2 border-violet-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-5 h-5 text-violet-600" />
                  <h3 className="font-semibold text-gray-900">Walk Together</h3>
                </div>
                <p className="text-gray-600 text-sm">A leisurely walk allows for natural, flowing conversation.</p>
              </div>
              
              <div className="bg-white border-2 border-violet-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Heart className="w-5 h-5 text-violet-600" />
                  <h3 className="font-semibold text-gray-900">Shared Activity</h3>
                </div>
                <p className="text-gray-600 text-sm">Cook together, do a puzzle, or try a new hobby side by side.</p>
              </div>
              
              <div className="bg-white border-2 border-violet-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-5 h-5 text-violet-600" />
                  <h3 className="font-semibold text-gray-900">Phone Call</h3>
                </div>
                <p className="text-gray-600 text-sm">For long-distance connections, a dedicated phone date works well.</p>
              </div>
            </div>

            <div className="bg-red-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-red-800 mb-4 flex items-center gap-2">
                <Smartphone className="w-5 h-5" />
                The Phone Rule
              </h2>
              <p className="text-gray-700 mb-4">
                The most important part of quality time is putting your phone away. Here's why:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>• Even a phone on the table reduces connection quality</li>
                <li>• Checking notifications signals the other person isn't priority</li>
                <li>• You can't be fully present when half-expecting a notification</li>
                <li>• One hour without your phone won't cause any emergencies</li>
              </ul>
              <p className="text-red-700 font-medium mt-4">
                Put your phone on silent, face down, or leave it in your bag/car.
              </p>
            </div>

            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">Conversation Starters</h2>
            
            <div className="space-y-3 mb-8">
              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-violet-400">
                <p className="text-gray-700">"What's been the highlight of your week/month?"</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-violet-400">
                <p className="text-gray-700">"What are you most looking forward to right now?"</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-violet-400">
                <p className="text-gray-700">"What's something you've been thinking about lately?"</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-violet-400">
                <p className="text-gray-700">"How can I better support you right now?"</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-violet-100 to-purple-100 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-violet-800 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Being Fully Present
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Make eye contact when they're speaking</li>
                <li>• Listen to understand, not to respond</li>
                <li>• Ask follow-up questions to show interest</li>
                <li>• Share your own thoughts and feelings openly</li>
                <li>• Notice and appreciate this moment together</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
